import React from 'react';
import { Box, BoxProps, Alert } from '@mui/material';
import { Role } from '../../auth/types/roles';
import { useHasRole } from '../../hooks/useHasRole';

/**
 * RoleBasedSection Component
 *
 * A container that conditionally renders its children based on user roles.
 * Useful for showing/hiding entire sections of UI.
 *
 * @example
 * ```tsx
 * // Only managers see this section
 * <RoleBasedSection allowedRoles={Role.MANAGER}>
 *   <AdminPanel />
 * </RoleBasedSection>
 *
 * // Multiple roles can see this section
 * <RoleBasedSection allowedRoles={[Role.RECEPTIONIST, Role.CLINICIAN]}>
 *   <BookingPanel />
 * </RoleBasedSection>
 *
 * // Show a message when user doesn't have access
 * <RoleBasedSection
 *   allowedRoles={Role.MANAGER}
 *   showMessageWhenDenied
 *   deniedMessage="Only managers can view financial reports"
 * >
 *   <FinancialReports />
 * </RoleBasedSection>
 *
 * // Wrap in a custom container
 * <RoleBasedSection
 *   allowedRoles={Role.CLINICIAN}
 *   containerProps={{ p: 3, bgcolor: 'background.paper', borderRadius: 2 }}
 * >
 *   <ClinicalTools />
 * </RoleBasedSection>
 * ```
 */

export interface RoleBasedSectionProps {
  /** Role(s) required to view this section */
  allowedRoles?: Role | readonly Role[];

  /** Children to render if user has required role */
  children: React.ReactNode;

  /**
   * If true, shows a message when user doesn't have access.
   * If false, renders nothing.
   * Default: false
   */
  showMessageWhenDenied?: boolean;

  /** Custom message to show when access is denied */
  deniedMessage?: string;

  /** Props to pass to the container Box component */
  containerProps?: BoxProps;

  /** If true, always wraps children in a Box (even when no containerProps) */
  alwaysWrap?: boolean;
}

export function RoleBasedSection({
  allowedRoles,
  children,
  showMessageWhenDenied = false,
  deniedMessage = 'You do not have permission to view this content',
  containerProps,
  alwaysWrap = false,
}: RoleBasedSectionProps) {
  const hasRequiredRole = useHasRole(allowedRoles as any);

  // If no roles specified, always show content
  if (!allowedRoles) {
    if (containerProps || alwaysWrap) {
      return <Box {...containerProps}>{children}</Box>;
    }
    return <>{children}</>;
  }

  // User doesn't have required role
  if (!hasRequiredRole) {
    if (showMessageWhenDenied) {
      return (
        <Alert severity="warning" sx={{ my: 2 }}>
          {deniedMessage}
        </Alert>
      );
    }
    return null;
  }

  // User has required role - render children
  if (containerProps || alwaysWrap) {
    return <Box {...containerProps}>{children}</Box>;
  }

  return <>{children}</>;
}

export default RoleBasedSection;
